<script setup lang="ts">
import { PageMeta } from '@/@vxapp/@Enums/PageMeta'
import { setPageTitle } from '@/@core/utils/pageMetaUtils'
import { cardActionCatalog } from './cardActionCatalog'

setPageTitle('Card actions')
definePage({ meta: PageMeta.Dashboard.ecommerce.meta })

const refetchData = (hideOverlay: () => void) => {
  setTimeout(hideOverlay, 3000)
}
</script>

<template>
  <main class="card-actions-page">
    <header class="card-actions-hero">
      <div class="card-actions-hero__eyebrow">Interactive canvas · 03 actions</div>
      <h1>Cards that respond to the moment.</h1>
      <p>
        Explore the three controls available to every action card, then test each focused
        configuration in the live component gallery.
      </p>
    </header>

    <section
      aria-labelledby="action-catalog-title"
      class="card-actions-catalog"
      data-testid="card-action-catalog"
    >
      <div class="card-actions-section-heading">
        <div>
          <div class="card-actions-section-heading__index">01 / Action system</div>
          <h2 id="action-catalog-title">One surface, three precise controls.</h2>
        </div>
        <p>Each control keeps the user inside the current canvas and preserves page context.</p>
      </div>

      <div class="card-actions-catalog__grid">
        <article
          v-for="(item, index) in cardActionCatalog"
          :key="item.icon"
          class="card-action-record"
          :data-testid="`card-action-record-${index + 1}`"
        >
          <div class="card-action-record__index">0{{ index + 1 }}</div>
          <VIcon
            class="card-action-record__icon"
            size="24"
            :icon="item.icon"
          />
          <div>
            <h3>{{ item.action }}</h3>
            <p>{{ item.description }}</p>
          </div>
          <code>{{ item.shortcut }}</code>
        </article>
      </div>
    </section>

    <section
      aria-labelledby="live-gallery-title"
      class="card-actions-gallery"
    >
      <div class="card-actions-section-heading">
        <div>
          <div class="card-actions-section-heading__index">02 / Live components</div>
          <h2 id="live-gallery-title">Try every state in place.</h2>
        </div>
        <p>Collapse, refresh, or remove the cards below to see the component contract in action.</p>
      </div>

      <AppCardActions
        class="card-actions-demo card-actions-demo--hero"
        data-testid="card-actions-all"
        title="All card actions"
        @refresh="refetchData"
      >
        <VCardText class="card-actions-demo__copy">
          <span class="card-actions-demo__label">Complete control set</span>
          <p>Use this card to compare collapse, refresh, and remove behavior in one place.</p>
        </VCardText>
      </AppCardActions>

      <div class="card-actions-gallery__grid">
        <AppCardActions
          action-collapsed
          class="card-actions-demo"
          data-testid="card-actions-collapse"
          title="Collapsible"
        >
          <VCardText class="card-actions-demo__copy">
            <span class="card-actions-demo__label">Focus the canvas</span>
            <p>
              Add <code>actionCollapse</code> when dense workspaces need a reversible way to hide
              detail.
            </p>
          </VCardText>
        </AppCardActions>

        <AppCardActions
          action-refresh
          class="card-actions-demo"
          data-testid="card-actions-refresh"
          title="Refresh content"
          @refresh="refetchData"
        >
          <VCardText class="card-actions-demo__copy">
            <span class="card-actions-demo__label">Update in context</span>
            <p>
              Add <code>actionRefresh</code> to show contained progress while content is refreshed.
            </p>
          </VCardText>
        </AppCardActions>

        <AppCardActions
          action-remove
          class="card-actions-demo"
          data-testid="card-actions-remove"
          title="Remove card"
        >
          <VCardText class="card-actions-demo__copy">
            <span class="card-actions-demo__label">Edit the composition</span>
            <p>
              Add <code>actionRemove</code> when a user can dismiss an optional card from the
              current page.
            </p>
          </VCardText>
        </AppCardActions>

        <AppCardActions
          class="card-actions-demo"
          data-testid="card-actions-slots"
          title="Slots"
          @refresh="refetchData"
        >
          <template #before-actions>
            <VChip
              class="me-3"
              color="primary"
              size="small"
            >
              3 updates
            </VChip>
          </template>

          <VCardText class="card-actions-demo__copy">
            <span class="card-actions-demo__label">Extend the control rail</span>
            <p>
              <code>app-card-actions</code> provides <code>before-actions</code> and
              <code>after-actions</code> slots for contextual tools.
            </p>
          </VCardText>
        </AppCardActions>
      </div>
    </section>
  </main>
</template>

<style scoped lang="scss">
.card-actions-page {
  --card-actions-accent: #0099ff;
  --card-actions-surface: #090909;
  --card-actions-muted: #a6a6a6;

  overflow: hidden;
  border-radius: 15px;
  background: radial-gradient(circle at 82% 6%, rgb(0 153 255 / 13%), transparent 27rem), #000;
  color: #fff;
  font-family:
    Inter,
    system-ui,
    -apple-system,
    'Segoe UI',
    sans-serif;
  padding-block: clamp(3rem, 7vw, 6rem);
  padding-inline: clamp(1.25rem, 5vw, 4.5rem);
}

.card-actions-hero {
  max-inline-size: 860px;
  padding-block-end: clamp(4.5rem, 9vw, 8rem);

  h1 {
    max-inline-size: 760px;
    margin-block: 1rem 1.5rem;
    font-size: clamp(3rem, 8vw, 6.85rem);
    font-weight: 500;
    letter-spacing: clamp(-3.5px, -0.05em, -1.8px);
    line-height: 0.89;
  }

  p {
    max-inline-size: 620px;
    margin: 0;
    color: var(--card-actions-muted);
    font-size: clamp(1rem, 2vw, 1.125rem);
    line-height: 1.55;
  }
}

.card-actions-hero__eyebrow,
.card-actions-section-heading__index,
.card-actions-demo__label {
  color: var(--card-actions-accent);
  font-family: 'Azeret Mono', ui-monospace, monospace;
  font-size: 0.7rem;
  letter-spacing: 0.08em;
  text-transform: uppercase;
}

.card-actions-catalog,
.card-actions-gallery {
  max-inline-size: 1200px;
  margin-inline: auto;
}

.card-actions-gallery {
  padding-block-start: clamp(5rem, 10vw, 8rem);
}

.card-actions-section-heading {
  display: grid;
  align-items: end;
  gap: 1.5rem;
  grid-template-columns: minmax(0, 1.5fr) minmax(16rem, 0.7fr);
  margin-block-end: 2rem;

  h2 {
    margin-block: 0.75rem 0;
    font-size: clamp(2rem, 4.5vw, 3.875rem);
    font-weight: 500;
    letter-spacing: -0.05em;
    line-height: 1;
  }

  p {
    margin: 0;
    color: var(--card-actions-muted);
    line-height: 1.6;
  }
}

.card-actions-catalog__grid,
.card-actions-gallery__grid {
  display: grid;
  gap: 1px;
  background: rgb(0 153 255 / 22%);
  box-shadow: 0 0 0 1px rgb(0 153 255 / 15%);
}

.card-actions-catalog__grid {
  grid-template-columns: repeat(3, minmax(0, 1fr));
}

.card-actions-gallery__grid {
  grid-template-columns: repeat(2, minmax(0, 1fr));
  margin-block-start: 1px;
}

.card-action-record {
  display: grid;
  min-inline-size: 0;
  min-block-size: 270px;
  align-content: space-between;
  padding: clamp(1.25rem, 3vw, 2rem);
  background: var(--card-actions-surface);

  h3 {
    margin-block: 2.5rem 0.65rem;
    color: #fff;
    font-size: 1.375rem;
    letter-spacing: -0.04em;
  }

  p {
    margin: 0;
    color: var(--card-actions-muted);
    font-size: 0.875rem;
    line-height: 1.6;
  }

  code {
    inline-size: fit-content;
    margin-block-start: 1.5rem;
    border-radius: 999px;
    background: rgb(255 255 255 / 10%);
    color: #fff;
    font-size: 0.75rem;
    padding-block: 0.4rem;
    padding-inline: 0.7rem;
  }
}

.card-action-record__index {
  color: var(--card-actions-muted);
  font-family: 'Azeret Mono', ui-monospace, monospace;
  font-size: 0.7rem;
}

.card-action-record__icon {
  margin-block-start: 2rem;
  color: var(--card-actions-accent);
}

.card-actions-demo {
  border-radius: 0 !important;
  background: var(--card-actions-surface) !important;
  color: #fff !important;
  box-shadow: inset 0 0.5px 0 rgb(255 255 255 / 10%) !important;

  :deep(.v-card-title) {
    color: #fff;
    font-size: 1.25rem;
    letter-spacing: -0.04em;
  }

  :deep(.v-overlay) {
    background: rgb(0 0 0 / 76%);
  }
}

:global(.card-actions-page .card-actions-demo .v-btn) {
  min-width: 48px !important;
  min-height: 48px !important;
  width: 48px;
  height: 48px !important;
  border-radius: 999px;
  color: #fff;
}

.card-actions-demo--hero {
  box-shadow: 0 0 0 1px rgb(0 153 255 / 45%) !important;
}

.card-actions-demo__copy {
  min-block-size: 150px;

  p {
    max-inline-size: 520px;
    margin-block: 1rem 0;
    color: var(--card-actions-muted);
    line-height: 1.6;
  }

  code {
    border-radius: 4px;
    background: rgb(255 255 255 / 10%);
    color: #fff;
    padding-inline: 0.3rem;
  }
}

@media (max-width: 809px) {
  .card-actions-page {
    border-radius: 0;
    margin-inline: -1rem;
  }

  .card-actions-section-heading,
  .card-actions-catalog__grid,
  .card-actions-gallery__grid {
    grid-template-columns: minmax(0, 1fr);
  }

  .card-action-record {
    min-block-size: 230px;
  }

  .card-actions-demo__copy {
    min-block-size: auto;
  }
}
</style>
